import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Alert,
  IconButton,
  CircularProgress
} from '@mui/material'
import DataTable from 'react-data-table-component'
import orderBy from 'lodash/orderBy'
import { getAllVendorWithdrawRequests } from '../apollo/queries'
import { processVendorPayout } from '../apollo/mutations'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import SearchIcon from '@mui/icons-material/Search'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PendingIcon from '@mui/icons-material/Pending'
import CloseIcon from '@mui/icons-material/Close'
import CustomLoader from '../components/Loader/CustomLoader'

const ALL_VENDOR_WITHDRAW_REQUESTS = gql`${getAllVendorWithdrawRequests}`
const PROCESS_PAYOUT = gql`${processVendorPayout}`

const statusConfig = {
  REQUESTED: { bg: '#fef3c7', color: '#92400e', border: '#fde68a', label: 'Requested' },
  APPROVED: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe', label: 'Approved' },
  TRANSFERRED: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0', label: 'Transferred' },
  CANCELLED: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca', label: 'Cancelled' }
}

const VendorPayouts = () => {
  const [processDialog, setProcessDialog] = useState(null)
  const [newStatus, setNewStatus] = useState('APPROVED')
  const [adminNote, setAdminNote] = useState('')
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const { data, loading, refetch } = useQuery(ALL_VENDOR_WITHDRAW_REQUESTS)
  const requests = data?.allVendorWithdrawRequests ?? []

  const [processMutation, { loading: processLoading }] = useMutation(PROCESS_PAYOUT, {
    onCompleted: () => {
      setSuccess('Payout request updated successfully')
      setProcessDialog(null)
      setAdminNote('')
      refetch()
      setTimeout(() => setSuccess(''), 5000)
    },
    onError: err => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  const handleProcess = () => {
    processMutation({ variables: { id: processDialog._id, status: newStatus, adminNote: adminNote || null } })
  }

  const regex = searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered = searchQuery.length < 3 ? requests : requests.filter(r =>
    (r.restaurantName || r.restaurantId || '').toLowerCase().search(regex) > -1 ||
    r.status?.toLowerCase().search(regex) > -1
  )

  const pendingCount = requests.filter(r => r.status === 'REQUESTED').length
  const approvedCount = requests.filter(r => r.status === 'APPROVED').length
  const totalAmount = requests.filter(r => r.status === 'REQUESTED').reduce((sum, r) => sum + (r.amount || 0), 0)

  const columns = [
    {
      name: 'REQUEST ID', hide: 'md', minWidth: '140px',
      cell: row => <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#1e293b', fontFamily: 'monospace' }}>#{row._id?.slice(-8).toUpperCase()}</Typography>
    },
    {
      name: 'RESTAURANT', sortable: true, selector: row => row.restaurantName, minWidth: '180px',
      cell: row => (
        <Box sx={{ py: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.83rem', color: '#1e293b' }}>{row.restaurantName || row.restaurantId}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.2 }}>{new Date(row.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Typography>
        </Box>
      )
    },
    {
      name: 'AMOUNT', sortable: true, selector: row => row.amount, minWidth: '120px',
      cell: row => <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>${(row.amount || 0).toFixed(2)}</Typography>
    },
    {
      name: 'STATUS', minWidth: '130px',
      cell: row => {
        const sc = statusConfig[row.status] ?? { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', label: row.status }
        return <Chip label={sc.label} size="small" sx={{ bgcolor: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, fontWeight: 700, fontSize: '0.7rem', height: 24 }} />
      }
    },
    {
      name: 'VENDOR NOTE', hide: 'sm', minWidth: '180px',
      cell: row => <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{row.requestNote || '—'}</Typography>
    },
    {
      name: 'ADMIN NOTE', hide: 'sm', minWidth: '180px',
      cell: row => <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{row.adminNote || '—'}</Typography>
    },
    {
      name: 'ACTION', right: true, minWidth: '120px',
      cell: row => (
        (row.status === 'REQUESTED' || row.status === 'APPROVED') ? (
          <Button size="small" variant="outlined"
            onClick={() => { setProcessDialog(row); setNewStatus(row.status === 'REQUESTED' ? 'APPROVED' : 'TRANSFERRED') }}
            sx={{ fontSize: '0.75rem', borderRadius: '8px', textTransform: 'none', fontWeight: 600, borderColor: '#3b82f6', color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff', borderColor: '#2563eb' } }}>
            Process
          </Button>
        ) : null
      )
    }
  ]

  return (
    <>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AccountBalanceWalletIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em' }}>Vendor Payouts</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Review and process vendor payout requests</Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        {success && <Alert severity="success" variant="filled" sx={{ mb: 2, borderRadius: '12px' }}>{success}</Alert>}
        {error && <Alert severity="error" variant="filled" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert>}

        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          {[
            { label: 'Pending Requests', value: pendingCount, icon: <PendingIcon sx={{ fontSize: 22 }} />, bg: 'linear-gradient(135deg,#fef3c7,#fde68a)', border: '#fde68a', iconBg: '#fef9c310', iconBorder: '#fde68a30', color: '#92400e' },
            { label: 'Approved', value: approvedCount, icon: <CheckCircleIcon sx={{ fontSize: 22 }} />, bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)', border: '#bfdbfe', iconBg: '#3b82f618', iconBorder: '#3b82f630', color: '#1e40af' },
            { label: 'Pending Amount', value: `$${totalAmount.toFixed(2)}`, icon: <AccountBalanceWalletIcon sx={{ fontSize: 22 }} />, bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '#bbf7d0', iconBg: '#22c55e18', iconBorder: '#22c55e30', color: '#166534' }
          ].map(stat => (
            <Paper key={stat.label} elevation={0} sx={{ background: stat.bg, border: `1px solid ${stat.border}`, borderRadius: '16px', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', minWidth: 180 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: stat.iconBg, border: `1px solid ${stat.iconBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: stat.color, flexShrink: 0 }}>{stat.icon}</Box>
              <Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{stat.value}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500, mt: 0.2 }}>{stat.label}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
              All Requests <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>({filtered.length})</Typography>
            </Typography>
            <TextField size="small" placeholder="Search requests..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>, sx: { borderRadius: '10px', fontSize: '0.83rem', bgcolor: '#fff' } }}
              sx={{ width: 260 }} />
          </Box>
          {loading ? <CustomLoader /> : (
            <DataTable columns={columns} data={filtered} pagination noHeader
              sortFunction={(rows, field, dir) => orderBy(rows, row => row[field]?.toString().toLowerCase(), dir)}
              customStyles={{ headRow: { style: { backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', minHeight: '44px' } }, headCells: { style: { fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' } }, rows: { style: { borderBottom: '1px solid #f1f5f9', minHeight: '64px', '&:hover': { backgroundColor: '#f8fafc' } } }, cells: { style: { paddingLeft: '16px', paddingRight: '16px' } } }}
              noDataComponent={<Box py={8} textAlign="center"><AccountBalanceWalletIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} /><Typography sx={{ color: '#64748b' }}>No payout requests</Typography></Box>}
            />
          )}
        </Paper>
      </Container>

      <Dialog open={!!processDialog} onClose={() => setProcessDialog(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">Process Payout Request</Typography>
          <IconButton size="small" onClick={() => setProcessDialog(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Box sx={{ mb: 2.5, p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: '0.83rem', color: '#64748b' }}>Restaurant</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>{processDialog?.restaurantName}</Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#3b82f6', mt: 0.5 }}>${processDialog?.amount?.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>Update Status</Typography>
            <Select fullWidth value={newStatus} onChange={e => setNewStatus(e.target.value)} size="small"
              sx={{ borderRadius: '10px', fontSize: '0.9rem' }}>
              <MenuItem value="APPROVED">Approve</MenuItem>
              <MenuItem value="TRANSFERRED">Mark as Transferred</MenuItem>
              <MenuItem value="CANCELLED">Cancel Request</MenuItem>
            </Select>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>Admin Note (optional)</Typography>
            <TextField fullWidth size="small" placeholder="Add a note..." value={adminNote} onChange={e => setAdminNote(e.target.value)} multiline rows={2}
              InputProps={{ sx: { borderRadius: '10px', fontSize: '0.9rem' } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button onClick={() => setProcessDialog(null)} sx={{ borderRadius: '10px', textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleProcess} disabled={processLoading}
            startIcon={processLoading ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3 }}>
            {processLoading ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default VendorPayouts